// @LTG-ID: F-050
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-050,
//        source `Journals/what-is-my-business-worth.html` lines 887-1240
//        (article body + figures + table + callouts + sources).
// @CHANGED: 2026-02 session_004 (CHG-0059)
// @OWNER: web
//
// F-050 — `/journals/what-is-my-business-worth` route.
// Brief 01 of The KGOB Journals: "What is my business actually worth?"
//
// Composition pattern (per ORCH-0050 A3):
//   <BriefPage frontmatter sources endCta readNext>
//     {/* At-a-Glance card */}
//     {/* article body — h2/p/lists/figures/callouts/quote/table inline */}
//   </BriefPage>
//
// Editorial accent: Source Serif 4 + gold are scoped to `.ltg-brief-page` in
// `brief-page.css` so /refund-policy and /(landing) keep Geist + teal.
//
// Figure components are imported individually so future briefs can reuse
// `<ThreeApproachesTriptych>` without re-declaring its SVG.

import BriefPage from "../components/journals/BriefPage";
import ThreeApproachesTriptych from "../components/journals/infographics/ThreeApproachesTriptych";
import SdeToEbitdaInflection from "../components/journals/infographics/SdeToEbitdaInflection";
import SameEbitdaDifferentMultiple from "../components/journals/infographics/SameEbitdaDifferentMultiple";
import ExternalCTA from "../lib/external-cta";
import {
    BRIEF_01_FRONTMATTER,
    BRIEF_01_AT_A_GLANCE,
    BRIEF_01_SOURCES,
    BRIEF_01_END_CTA,
    BRIEF_01_READ_NEXT,
} from "../data/brief-01";

export const Brief01 = () => {
    return (
        <BriefPage
            frontmatter={BRIEF_01_FRONTMATTER}
            sources={BRIEF_01_SOURCES}
            endCta={BRIEF_01_END_CTA}
            readNext={BRIEF_01_READ_NEXT}
        >
            {/* At-a-Glance */}
            <aside
                className="ltg-brief-glance"
                data-testid="brief01-at-a-glance"
            >
                <span className="ltg-brief-glance-label">At a glance</span>
                <dl>
                    {BRIEF_01_AT_A_GLANCE.map((row, idx) => (
                        <div key={idx} style={{ display: "contents" }}>
                            <dt>{row.dt}</dt>
                            <dd
                                dangerouslySetInnerHTML={{ __html: row.dd }}
                            />
                        </div>
                    ))}
                </dl>
            </aside>

            <p
                className="ltg-brief-lead-graf"
                data-testid="brief01-lead-graf"
            >
                Most owners learn what their business is worth by hearing what
                a competitor sold for, then assuming the same multiple
                applies. That is not how valuation works. A defensible answer
                requires three things: a normalized earnings number, a
                defensible multiple drawn from market evidence, and an honest
                reading of the company's risk profile. This article walks
                through that process the way a CPA would explain it to a
                client at the kitchen table.
            </p>

            <h2>
                Why owners overestimate value, and why it costs them at the
                table
            </h2>
            <p>
                The single most-common source of disappointment in a sale
                process is not the buyer; it is the seller's prior
                expectation. Owners arrive at the table with a number they
                have carried for years, anchored on a competitor's sale, a
                banker's casual comment, or a back-of-the-envelope multiple
                applied to top-line revenue. The actual market evidence is
                almost always lower, and the gap is wide enough to kill
                deals.
            </p>

            <div
                className="ltg-brief-stat-callout"
                data-testid="brief01-stat-callout"
            >
                <div className="ltg-brief-stat-figure">
                    <em>80%</em> of owners' net worth sits in the business.
                </div>
                <p className="ltg-brief-stat-text">
                    Only 27% of Baby Boomer owners have a formal valuation.
                    The two facts together explain why the discovery of
                    actual market price arrives so late and so hard in the
                    sale process.
                </p>
                <cite className="ltg-brief-stat-cite">
                    Exit Planning Institute, 2023 National State of Owner
                    Readiness Report
                </cite>
            </div>

            <p>
                The Pepperdine Private Capital Markets Report tracks this gap
                directly. In its 2025 edition, Dr. Craig R. Everett's survey
                of credentialed appraisers found that roughly 31% of
                engagements ended without a transaction, with the valuation
                gap driving 26% of those failures. In about 84% of those
                gaps, the spread between buyer and seller expectations sat
                between 11% and 30%. That is the gap a defensible valuation,
                performed early, would have closed before the process began.
            </p>

            <h2>The three approaches to value, in one paragraph each</h2>
            <p>
                Every credentialed valuation in the United States, regardless
                of who performs it, operates within a framework of three
                approaches. The <strong>income approach</strong> values the
                business at the present value of its expected future cash
                flows, using either discounted cash flow analysis or a
                capitalization of earnings method. The{" "}
                <strong>market approach</strong> values the business by
                comparing it to actual transactions involving similar
                companies, drawing from databases like BVR DealStats, Pratt's
                Stats, and the public guideline-company method. The{" "}
                <strong>asset approach</strong> values the business at the
                adjusted net asset value of its underlying balance sheet,
                which matters most for asset-heavy or holding companies and
                rarely drives an operating business's value.
            </p>
            <p>
                A defensible valuation does not pick one. It uses all three
                where applicable, weights them by the facts of the
                engagement, and reconciles to a conclusion. The AICPA's
                Statement on Standards for Valuation Services No. 1 expects
                appraisers to consider each approach and document why each
                was used or excluded. Brief 3 in this library walks through
                each approach in depth; for the purpose of this primer, the
                point is that a number without a method is a guess, and a
                method without a reconciliation is incomplete.
            </p>

            <ThreeApproachesTriptych />

            <h2>SDE versus EBITDA: which one applies to your business</h2>
            <p>
                Cash flow is the operative number in nearly every
                private-business valuation, but two different definitions of
                cash flow apply at different sizes of business.{" "}
                <strong>Seller's Discretionary Earnings</strong>, or SDE,
                measures what an owner-operator earns from the business: net
                income plus interest, taxes, depreciation, amortization, one
                owner's salary and benefits, and one round of discretionary
                add-backs. <strong>EBITDA</strong>, or earnings before
                interest, taxes, depreciation, and amortization, measures
                what a corporate buyer would earn from running the business
                with a hired manager in the owner's seat.
            </p>
            <p>
                The inflection point sits around $1M to $2M of normalized
                earnings. Below it, the buyer pool is dominated by
                individual owner-operators, SBA-financed acquisitions, and
                small search funds, all of whom underwrite to SDE. Above it,
                the buyer pool tilts toward private equity, family offices,
                and strategic acquirers, who underwrite to EBITDA with a
                market-rate manager replacement assumed. Which number
                applies to your business changes who your buyers are, how
                they finance, and what add-backs survive their Quality of
                Earnings analysis.
            </p>

            <table
                className="ltg-brief-table"
                data-testid="brief01-sde-ebitda-table"
            >
                <caption>
                    The SDE vs EBITDA inflection, by typical 2025 buyer pool
                </caption>
                <thead>
                    <tr>
                        <th>Business size (normalized earnings)</th>
                        <th>Cash flow used</th>
                        <th>Typical buyer pool</th>
                        <th>Typical 2025 multiple</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Under $500K SDE</td>
                        <td>SDE</td>
                        <td>Individual buyers, SBA-financed</td>
                        <td>1.5x to 2.5x SDE</td>
                    </tr>
                    <tr>
                        <td>$500K to $1M SDE</td>
                        <td>SDE</td>
                        <td>Individuals, search funds, small PE</td>
                        <td>2.5x to 3.5x SDE</td>
                    </tr>
                    <tr>
                        <td>$1M to $2M EBITDA</td>
                        <td>Mostly EBITDA (transition zone)</td>
                        <td>Small PE, family offices, strategics</td>
                        <td>4.0x to 6.0x EBITDA</td>
                    </tr>
                    <tr>
                        <td>$2M to $5M EBITDA</td>
                        <td>EBITDA</td>
                        <td>PE platforms, family offices</td>
                        <td>5.0x to 7.0x EBITDA</td>
                    </tr>
                    <tr>
                        <td>$5M to $50M EBITDA</td>
                        <td>EBITDA (with QofE)</td>
                        <td>
                            PE platforms, strategics, public acquirers
                        </td>
                        <td>6.0x to 10.0x+ EBITDA</td>
                    </tr>
                </tbody>
            </table>
            <p className="ltg-brief-table-source">
                Source: synthesis of IBBA Market Pulse Q4 2025, BizBuySell
                2025 Year-End Insight Report, GF Data Resources Q1 2025.
            </p>

            <SdeToEbitdaInflection />

            <h2>The multiple is a conclusion, not an input</h2>
            <p>
                Every owner asks for a multiple. Most ask the wrong question.
                A multiple is not a coefficient you apply; it is a conclusion
                you arrive at after the work is done. Credentialed appraisers
                derive multiples from databases of completed transactions,
                adjusted for size, sector, growth profile, customer
                concentration, recurring revenue mix, and a long list of
                other risk factors. The Pepperdine PCM 2025 survey found
                that the guideline-transactions method carries 33% average
                weighting in private-business appraisals, the largest of any
                single methodology.
            </p>

            <blockquote
                className="ltg-brief-pull-quote"
                data-testid="brief01-pull-quote"
            >
                <p>
                    "The honest answer to 'what is my business worth' starts
                    with 'tell me about your customer concentration, your
                    recurring revenue, your second-in-command, and your last
                    three years of EBITDA.' The multiple is the last thing we
                    calculate, not the first."
                </p>
                <cite>
                    Sara F. Gonzalez, CPA &middot; Managing Partner, KGOB
                </cite>
            </blockquote>

            <p>
                When you see a published "industry average" of 4x or 6x,
                that figure is a median across thousands of transactions
                with widely varying risk profiles. Your business might trade
                at the median, or it might trade two turns lower, or two
                turns higher. The work of valuation is figuring out which.
            </p>

            <h2>Normalizations and add-backs: legitimate versus aggressive</h2>
            <p>
                Tax returns understate earnings for almost every
                owner-operated business, because the owner has been managing
                the business for tax efficiency rather than for sale
                presentation. Normalizing those tax returns to "what the
                business actually earns, separated from how the owner runs
                it" is the first technical step of any defensible valuation.
                Some add-backs are universally accepted; others are routinely
                rejected in Quality of Earnings analysis. Knowing the
                difference, in advance, prevents painful surprises later in
                the diligence process.
            </p>

            <p>
                <strong>Add-backs buyers typically accept:</strong>
            </p>
            <ul>
                <li>
                    One owner's compensation, replaced with a market-rate
                    manager salary
                </li>
                <li>
                    One-time professional fees not expected to recur (the
                    sale process itself, one-off litigation)
                </li>
                <li>
                    Personal vehicles, personal travel, personal insurance
                    run through the business
                </li>
                <li>
                    Family members on payroll who do not work in the
                    business
                </li>
                <li>
                    Pandemic-era one-time costs (PPE, temporary remote-work
                    infrastructure)
                </li>
            </ul>

            <p>
                <strong>Add-backs buyers typically reject:</strong>
            </p>
            <ul>
                <li>
                    Aspirational cost savings ("if I cut these contracts,
                    EBITDA would be higher")
                </li>
                <li>
                    Revenue not yet booked but expected ("we are about to
                    land a big customer")
                </li>
                <li>
                    The "if you fired me" calculation that assumes the
                    business runs without replacement
                </li>
                <li>
                    Recurring discretionary spending presented as one-time
                </li>
                <li>
                    Owner perquisites that the next owner will also want or
                    require
                </li>
            </ul>

            <h2>Risk drives the multiple as much as size does</h2>
            <p>
                A $5M EBITDA business with three large levers right will
                trade at 8x. The same $5M EBITDA business with those levers
                wrong will trade at 5x. That spread is $15M of enterprise
                value on identical earnings, and the levers are not secrets.
                They are the ones every CPA firm and M&amp;A advisor has
                been watching for thirty years.
            </p>
            <p>The three highest-impact levers, in our experience:</p>
            <ol>
                <li>
                    <strong>Customer concentration.</strong> Any customer
                    above 20% of revenue triggers a detailed buyer review;
                    above 30%, some buyers decline the process entirely
                    (FOCUS Investment Banking, July 2025).
                </li>
                <li>
                    <strong>Owner dependency.</strong> If the business
                    cannot run for ninety days without you, you are selling
                    a job, not a business. The discount ranges from 10% to
                    30% depending on sector.
                </li>
                <li>
                    <strong>Recurring revenue.</strong> A $4M EBITDA HVAC
                    company with 60% recurring revenue trades at 8x to 9x.
                    The same business with 20% recurring trades at 5x to 6x.
                    That is $12M to $16M of enterprise value difference on
                    identical earnings (CT Acquisitions Manufacturing Report
                    2026).
                </li>
            </ol>

            <SameEbitdaDifferentMultiple />

            <div
                className="ltg-brief-inline-cta"
                data-testid="brief01-inline-cta"
            >
                <p className="ltg-brief-inline-cta-text">
                    See how your business scores on each of the ten value
                    levers, in a Strategic Snapshot signed by{" "}
                    <em>Sara</em>.
                </p>
                <ExternalCTA
                    ctaId="brief-01-order"
                    className="ltg-brief-inline-cta-btn"
                    data-testid="brief01-inline-cta-btn"
                >
                    Get Your Valuation →
                </ExternalCTA>
            </div>

            <h2>
                Indicative valuation versus formal appraisal: when each is
                enough
            </h2>
            <p>
                The AICPA's SSVS No. 1 distinguishes a{" "}
                <strong>valuation engagement</strong>, which produces a
                Conclusion of Value, from a{" "}
                <strong>calculation engagement</strong>, which produces a
                Calculated Value. The fee bands differ accordingly. A formal
                valuation engagement for a $5M business typically runs
                $7,500 to $15,000 and takes four to six weeks. A calculation
                engagement runs lower. A productized indicative valuation,
                like our Strategic Snapshot, runs $995 and produces an
                indicative range against the same methodological framework.
            </p>
            <p>
                Each tool has a use. A $995 indicative valuation answers{" "}
                <em>
                    what is the directional answer, before I make a planning
                    decision
                </em>
                . A formal appraisal answers{" "}
                <em>
                    what do I file with the IRS, with the court, with the
                    ESOP trustee, with the SBA lender
                </em>
                . The mistake is using one to do the job of the other.
                Brief 4 walks through that decision tree in more detail.
            </p>

            <h2>
                Who is asking the question? IRS, lender, divorce court, buyer
            </h2>
            <p>
                The same business produces different values under different
                standards. Fair market value under IRS Revenue Ruling 59-60
                governs estate and gift tax matters. Investment value
                reflects what a specific buyer is willing to pay given their
                synergies. Fair value under state divorce statutes varies by
                jurisdiction and often strips the discounts that apply in
                tax matters. Liquidation value applies when continuity is
                not assumed.
            </p>
            <p>
                Before you ask what your business is worth, ask who is
                asking and why. The answer changes the methodology, the
                discounts, and the number. Brief 5 in this library walks
                through each of the five standards.
            </p>

            <h2>
                How LTG's Strategic Snapshot produces an indicative range
            </h2>
            <p>
                The Strategic Snapshot applies a productized methodology
                built by KGOB over thirty-eight years of valuation work
                across every sector a Main Street CPA firm serves: trades,
                restaurants, healthcare practices, manufacturers,
                professional services, and technology. The buyer supplies a
                small set of financial inputs (revenue, normalized earnings,
                growth, customer concentration, recurring revenue mix,
                sector code). The model applies pre-set computations and
                produces:
            </p>
            <ul>
                <li>
                    An indicative enterprise value range (low, median, top
                    quartile)
                </li>
                <li>A scorecard across the ten strategic value levers</li>
                <li>
                    The three priority moves we estimate would move your
                    multiple the most over a 24-month runway
                </li>
                <li>
                    A tax-efficiency review against federal and North
                    Carolina law
                </li>
            </ul>
            <p>
                The Snapshot is an <strong>indicative valuation</strong>,
                not a formal appraisal. It is intended to be the first
                conversation an owner has with the question, not the last.
                For SBA loan documentation, gift or estate filings, ESOP
                valuations, or litigation purposes, retain a credentialed
                business appraiser (ASA, ABV, CVA, or CBA). Brief 4 covers
                when the upgrade is required.
            </p>

            <h2>What to do with the number</h2>
            <p>
                Once you have a defensible range, three decisions follow.
                First, decide whether the range supports your retirement
                number after taxes and fees (Brief 10 walks through the
                freedom-point math). Second, decide which of the ten levers
                to move over a 24-month runway, in order of impact. Third,
                decide whether the business is at the right stage of
                readiness for the path you are considering: a third-party
                sale, an ESOP, a family transition, or a management buyout
                (Brief 11 compares the four paths).
            </p>
            <p>
                None of these decisions becomes easier with time. Owners who
                plan an exit 36 months in advance keep, on average,
                materially more of the after-tax proceeds than owners who
                react to an inbound offer. The market is reasonably
                efficient. The owners who do best in it are the ones who run
                their preparation on a longer clock than the buyers do.
            </p>

            <div
                className="ltg-brief-takeaway"
                data-testid="brief01-takeaway"
            >
                <span className="ltg-brief-takeaway-label">Takeaway</span>
                <p>
                    Your business is worth what a defensible buyer will pay
                    under current market conditions, supported by your
                    normalized earnings and your risk profile. Anything else
                    is a guess. The work is figuring out which.
                </p>
            </div>
        </BriefPage>
    );
};

export default Brief01;
